/**
 * LPAAS 发布自动化 —— 常量层（唯一出口）
 *
 * 依据：
 *   契约：lpaas-publish/前端对接文档-LPAAS发布自动化（一期）.md §7 状态字典 / §8 错误码
 *   方案：lpaas-publish/前端开发技术方案-LPAAS发布自动化（一期）.md §4
 *
 * 🔴 三条硬规矩（违反任一条即打回，见方案 §2.3）：
 *   1. 枚举值必须是后端字典的大写值（D3 的根治：小写态全部匹配不上 → 整列退化成灰 Tag）；
 *   2. 页面/组件禁止写 '发布失败' / 'PUBLISHING' 这类字面量，一律从这里取（D15 的根治）；
 *   3. 端点字符串只在 services/lpaasPublish.js 的 REAL_API_PATHS 里，本文件不放路径。
 *
 * 🚫 开发依据红线：旧《技术方案-LPAAS发布自动化（一期）.md》与 prototype.html 是 V2.1 口径
 *    （仍含「人工放行」「迭代级终止」「继续下一批」），与本文冲突时以本文为准。
 */

/* -------------------------------------------------------------------------- */
/* 路由（umi3 约定式：src/pages 目录即地址）                                     */
/* -------------------------------------------------------------------------- */

/**
 * 🔴 跨页传参只用 query，不用路由 params（:id）。
 * 全站 src/pages 无任何 $ 动态路由文件，`/detail/12` 在约定式路由下匹配不到 → 白屏（D4）。
 */
export const ROUTE = {
  LIST: '/technical/lpaasPublish',
  DETAIL: '/technical/lpaasPublish/detail',
  CONFIG: '/technical/lpaasPublish/config',
};

/** query 参数名（详情页刷新/分享都靠它） */
export const QUERY_KEY = {
  ITERATION_ID: 'iterationId',
  ANCHOR_BATCH_NO: 'anchorBatchNo',
};

/**
 * 新标签打开用的应用前缀。
 * ⚠️ 本仓库 .umirc.ts 没有 define PUBLIC_PATH（umi3 也不自动注入该全局），
 *    直接写 `${PUBLIC_PATH}` 会 ReferenceError。运行时 umi 会注入 window.routerBase（= base），
 *    取它、兜到 '/litta-palmer'，与站内其它 window.open 页面写法一致。
 */
export const APP_BASE_FALLBACK = '/litta-palmer';

/* -------------------------------------------------------------------------- */
/* 状态枚举（值 = 后端 status，对接文档 §7）                                     */
/* -------------------------------------------------------------------------- */

/** 迭代状态：对外 4 态。🔴 SUCCESS 可被追加批次重新激活回 PUBLISHING，列表不许冻结终态行 */
export const ITERATION_STATUS = {
  PUBLISHING: 'PUBLISHING',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  TERMINATED: 'TERMINATED',
};

/**
 * 迭代状态 → 中文。
 * 🔴 只给「筛选下拉的选项文案」用：列表/详情行内一律只用后端 `statusDisplay`
 *    （后端改文案前端不动）。下拉框没法向后端要「全部候选」，只能本地把 4 态列全。
 */
export const ITERATION_STATUS_LABEL = {
  [ITERATION_STATUS.PUBLISHING]: '发布中',
  [ITERATION_STATUS.SUCCESS]: '发布成功',
  [ITERATION_STATUS.FAILED]: '发布失败',
  [ITERATION_STATUS.TERMINATED]: '已终止',
};

/** 筛选下拉的 options（顺序固定 = 上面对象的书写顺序，避免每次渲染重排） */
export const ITERATION_STATUS_OPTIONS = Object.entries(
  ITERATION_STATUS_LABEL,
).map(([value, label]) => ({ value, label }));

/** 批次状态：TIMEOUT 对外并入失败口径展示（PRD 只画 4 个图标，不新增第五个） */
export const BATCH_STATUS = {
  ...ITERATION_STATUS,
  TIMEOUT: 'TIMEOUT',
};

/**
 * 服务状态：16 态 + UNKNOWN（映射不到 LPaaS 原值时为 UNKNOWN，🔴 不许塌成「进行中」或「失败」）。
 * 🔴 必须与后端 `LpaasServiceStatusEnum` 逐项对齐（值就是后端下发的 code）：
 *    终态 6 个 = SUCCESS / FAILED / TERMINATED / ABORTED / TIMEOUT / SKIPPED，其余都是非终态。
 *    之前漏了 QUEUED / RUNNING / UNKNOWN_OUTCOME → 见下面 ACTIVE_SERVICE_STATUS 的事故说明。
 */
export const SERVICE_STATUS = {
  PENDING: 'PENDING',
  DISPATCHING: 'DISPATCHING',
  QUEUED: 'QUEUED',
  RUNNING: 'RUNNING',
  BUILDING: 'BUILDING',
  CHECKING_CONFIG: 'CHECKING_CONFIG',
  DEPLOYING: 'DEPLOYING',
  PAUSED: 'PAUSED',
  AUTH_EXPIRED: 'AUTH_EXPIRED',
  UNKNOWN_OUTCOME: 'UNKNOWN_OUTCOME',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  TERMINATED: 'TERMINATED',
  ABORTED: 'ABORTED',
  TIMEOUT: 'TIMEOUT',
  SKIPPED: 'SKIPPED',
  UNKNOWN: 'UNKNOWN',
};

/**
 * 🔴 「还在跑」集合：除终态都算，含 PAUSED / AUTH_EXPIRED / UNKNOWN —— 漏一个就少刷一次。
 * PAUSED 不是失败也不给「继续」按钮（AC-09）；UNKNOWN 后端已 WARN，前端照实显示并继续轮。
 *
 * 🔴 2026-10 补漏（线上 P2）：这里原来只有 8 个状态，漏了后端真会下发的
 *    QUEUED（LPaaS 侧排队）/ RUNNING（LPaaS 说在跑、阶段还没拉回来）/ UNKNOWN_OUTCOME（下发结果未知）。
 *    而 `pickPollInterval` 判「还有没有活跃服务」用的就是这张表 → 批次里全是这三个状态时
 *    会被算成「全终态」→ 间隔 0 → **轮询直接停**，页面看起来卡住不动（实质是前端不再取数）。
 *    两张表（本表 + SERVICE_STATUS）必须同源同改：新增状态时先加 SERVICE_STATUS，再加这里。
 *
 * ⚠️ 2026-10-08 起：**详情页轮询节奏不再看服务状态**（改看批次状态，见 `pickPollInterval`），
 *    所以「漏一个状态就少刷一次」这个坑已经结构性消除；本表保留作为「非终态」口径的唯一定义
 *    （`hasActiveService` 现仅单测消费，作为口径钉子保留，页面渲染不再使用）。
 *    同批删掉了 `RUNNING_SERVICE_STATUS`（那是 15s 慢档的分档表，随双频 Job 一起废弃）。
 */
export const ACTIVE_SERVICE_STATUS = [
  SERVICE_STATUS.PENDING,
  SERVICE_STATUS.DISPATCHING,
  SERVICE_STATUS.QUEUED,
  SERVICE_STATUS.RUNNING,
  SERVICE_STATUS.BUILDING,
  SERVICE_STATUS.CHECKING_CONFIG,
  SERVICE_STATUS.DEPLOYING,
  SERVICE_STATUS.PAUSED,
  SERVICE_STATUS.AUTH_EXPIRED,
  SERVICE_STATUS.UNKNOWN_OUTCOME,
  SERVICE_STATUS.UNKNOWN,
];

/** 阶段（节点）状态：后端 icon 优先，本表只兜样式类与缺省图标 */
export const STAGE_STATUS = {
  PENDING: 'PENDING',
  QUEUED: 'QUEUED',
  RUNNING: 'RUNNING',
  PAUSED: 'PAUSED',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  TERMINATED: 'TERMINATED',
  UNKNOWN: 'UNKNOWN',
};

/**
 * 授权门禁阶段（`hooks/useLpaasAuth.js` 的状态机，AuthGuideModal 按它渲染）。
 * 🔴 定义在常量层而不是 hook 里：组件层只允许 import constants/utils（方案 §2.3），
 *    放 hook 里会让「引导弹窗」这个纯展示组件反向依赖 services。
 */
export const AUTH_PHASE = {
  IDLE: 'idle',
  CHECKING: 'checking',
  NEED_AUTH: 'needAuth',
  AUTHORIZING: 'authorizing',
  OK: 'ok',
  TIMEOUT: 'timeout',
};

/**
 * 🔴 【临时方案】TEMPORARY-2026-10 手动token（国庆应急）—— **节后连同扫码链路恢复一起删**。
 *
 * 背景：应急期 LPaaS 正式鉴权接口与我方对不上（cli 通道的 token 打 {@code /api/edas/*} 一律 401），
 * 扫码链路节内跑不通，先用「手动粘贴登录态凭证」把后续链路解锁。
 *
 * 🔴 本常量里的字段名必须与后端一致；不自己写「是不是生产」的判断 ——
 *    入口能不能出现由后端 {@code /auth/check} 下发（Apollo 开关 + 非生产 profile），
 *    前端只负责“给了就显示”。
 */
export const MANUAL_TOKEN = {
  /** 凭证来源：手动填入（临时方案，收假后据此筛数据）
   * 🔴 后端 /auth/check 下发的 authSource 取值仅 'SCAN'（扫码，正式链路）与本值两种，
   *    扫码值由 services 层透传，不设常量（全仓无消费方）。
   */
  SOURCE_MANUAL: 'MANUAL',
};

/* -------------------------------------------------------------------------- */
/* 样式（色值只用 less 里已有的 tag-* 类，别自造第六种颜色）                       */
/* -------------------------------------------------------------------------- */

export const TAG = {
  PROCESSING: 'tag-processing',
  SUCCESS: 'tag-success',
  ERROR: 'tag-error',
  WARNING: 'tag-warning',
  DEFAULT: 'tag-default',
  IDLE: 'tag-idle',
};

/** 迭代状态 → Tag 类 + 缺省图标（图标正常取后端 statusIcon） */
export const ITERATION_TAG_COLOR = {
  [ITERATION_STATUS.PUBLISHING]: TAG.PROCESSING,
  [ITERATION_STATUS.SUCCESS]: TAG.SUCCESS,
  [ITERATION_STATUS.FAILED]: TAG.ERROR,
  [ITERATION_STATUS.TERMINATED]: TAG.WARNING,
  default: TAG.DEFAULT,
};

/** 批次状态 → Tag 类（TIMEOUT 按失败口径，见 §7.2） */
export const BATCH_TAG_COLOR = {
  ...ITERATION_TAG_COLOR,
  [BATCH_STATUS.TIMEOUT]: TAG.ERROR,
};

/** 服务状态 → Tag 类（对接文档 §7.3 的图标/色列） */
export const SERVICE_TAG_COLOR = {
  [SERVICE_STATUS.PENDING]: TAG.DEFAULT,
  [SERVICE_STATUS.DISPATCHING]: TAG.PROCESSING,
  [SERVICE_STATUS.QUEUED]: TAG.DEFAULT, // 后端 desc「等待中」+ 图标 ○，与 PENDING 同色
  [SERVICE_STATUS.RUNNING]: TAG.PROCESSING,
  [SERVICE_STATUS.BUILDING]: TAG.PROCESSING,
  [SERVICE_STATUS.CHECKING_CONFIG]: TAG.PROCESSING,
  [SERVICE_STATUS.DEPLOYING]: TAG.PROCESSING,
  [SERVICE_STATUS.PAUSED]: TAG.WARNING,
  [SERVICE_STATUS.AUTH_EXPIRED]: TAG.WARNING,
  [SERVICE_STATUS.UNKNOWN_OUTCOME]: TAG.WARNING, // 「我方不确定下发成功没」的异常态，与 AUTH_EXPIRED 同档
  [SERVICE_STATUS.SUCCESS]: TAG.SUCCESS,
  [SERVICE_STATUS.FAILED]: TAG.ERROR,
  [SERVICE_STATUS.TERMINATED]: TAG.DEFAULT,
  [SERVICE_STATUS.ABORTED]: TAG.DEFAULT,
  [SERVICE_STATUS.TIMEOUT]: TAG.ERROR,
  [SERVICE_STATUS.SKIPPED]: TAG.IDLE,
  [SERVICE_STATUS.UNKNOWN]: TAG.IDLE,
  default: TAG.DEFAULT,
};

/** 服务状态 → 缺省图标（后端 statusIcon 优先） */
export const SERVICE_ICON = {
  [SERVICE_STATUS.PENDING]: '○',
  [SERVICE_STATUS.DISPATCHING]: '◐',
  [SERVICE_STATUS.QUEUED]: '○',
  [SERVICE_STATUS.RUNNING]: '◐',
  [SERVICE_STATUS.BUILDING]: '◐',
  [SERVICE_STATUS.CHECKING_CONFIG]: '◐',
  [SERVICE_STATUS.DEPLOYING]: '◐',
  [SERVICE_STATUS.PAUSED]: '⏸',
  [SERVICE_STATUS.AUTH_EXPIRED]: '⚠',
  [SERVICE_STATUS.UNKNOWN_OUTCOME]: '⚠',
  [SERVICE_STATUS.SUCCESS]: '✔',
  [SERVICE_STATUS.FAILED]: '✕',
  [SERVICE_STATUS.TERMINATED]: '┃',
  [SERVICE_STATUS.ABORTED]: '┃',
  [SERVICE_STATUS.TIMEOUT]: '✕',
  [SERVICE_STATUS.SKIPPED]: '○',
  [SERVICE_STATUS.UNKNOWN]: '?',
};

/**
 * 阶段节点样式（StageProgress 用）。
 * 🔴 展示文案永远用后端 displayName，本表只提供颜色与缺省图标。
 */
export const STAGE_NODE_STYLE = {
  [STAGE_STATUS.PENDING]: { cls: TAG.DEFAULT, icon: '○' },
  [STAGE_STATUS.QUEUED]: { cls: TAG.DEFAULT, icon: '○' },
  [STAGE_STATUS.RUNNING]: { cls: TAG.PROCESSING, icon: '◐' },
  [STAGE_STATUS.PAUSED]: { cls: TAG.WARNING, icon: '⏸' },
  [STAGE_STATUS.SUCCESS]: { cls: TAG.SUCCESS, icon: '✔' },
  [STAGE_STATUS.FAILED]: { cls: TAG.ERROR, icon: '✕' },
  [STAGE_STATUS.TERMINATED]: { cls: TAG.DEFAULT, icon: '┃' },
  [STAGE_STATUS.UNKNOWN]: { cls: TAG.IDLE, icon: '?' },
  default: { cls: TAG.DEFAULT, icon: '○' },
};

/** 「停止中」中间态的展示类（⏳ 转圈 + 按钮 loading），🔴 只改展示不改状态文字语义 */
export const STOPPING_ICON = '⏳';

/* -------------------------------------------------------------------------- */
/* SKIPPED 行 tooltip（对接文档 §7.5，六条文案照抄，不许自行改写）                  */
/* -------------------------------------------------------------------------- */

export const BLOCK_REASON_TIP = {
  UPSTREAM_FAILED: '依赖的上游服务未发布成功，本次未开始',
  UPSTREAM_NOT_IN_BATCH: '依赖的上游不在本批次内，无法判定依赖已满足',
  APPID_NOT_WHITELISTED: '该应用未配置发布参数，请联系运维',
  NS_MISMATCH: '应用环境与所选分支不一致',
  FIELD_MISSING: '报告缺少必需字段',
  LPAAS_REJECTED: 'LPaaS 拒绝执行，原因见失败原文',
};

/** blockReason 不在字典里（或后端没给）时的兜底提示 */
export const BLOCK_REASON_UNKNOWN_TIP = '未提供跳过原因';

/* -------------------------------------------------------------------------- */
/* 轮询节奏（详情页「秒级给人看」的唯一来源）                                      */
/* -------------------------------------------------------------------------- */

/**
 * 详情页轮询参数。
 *
 * 🔴 2026-10-08 口径变更（依据《方案存档-LPAAS发布轮询改造（前端驱动+分钟级兜底）》§4.1）：
 *    原来分两档 —— 有「执行中」服务 5s、只有排队/等待 15s（对齐后端 5s/15s 双频 Job）。
 *    新方案把后端两个 Job 合成**一个 60s 兜底 Job**，前端轮询成了「秒级」的**唯一来源**，
 *    所以：**只要还有未收口的批次就统一 5s**，15s 慢档取消。
 *    为什么必须去掉慢档：排队 → 执行中的跃迁常在几秒内发生，15s 档会让用户「看着十几秒不动」，
 *    而 PRD 的「5 秒内更新」正是要修掉这个观感（后端 poll-on-read 有 3s 去重窗口，5s 档不会打爆 LPaaS）。
 *
 * 🔴 节奏只有两个来源：本常量兜底 + 后端下发 `frontPollSeconds.running` 覆盖
 *    （见 `detailLogic.pollIntervalMs`）。旧的 `queued` 字段本轮起**不再读**。
 * 🔴 「停轮」只由两件事决定：批次全部收口（数据派生）/ 页面关掉或切到后台（见 usePublishPolling 的四刹车）。
 */
export const POLL = {
  /** 有未收口批次（任一批次 PUBLISHING）= 统一 5s。🔴 判据是**批次状态**，不是服务状态 */
  ACTIVE_MS: 5000,
  /**
   * 累计上限，到点硬停并挂提示（【继续自动刷新】可手动续）。
   * 🔴 保留的理由：`PAUSED`（等人确认）/ `AUTH_EXPIRED`（等人扫码）都是**非终态**，
   *    方案 §7-1 明确「用户等几小时就白轮几小时」这档还没优化 → 前端先自保，别挂一夜还在打后端。
   */
  MAX_MS: 30 * 60 * 1000,
  FAIL_STOP_TIMES: 3, // 连续失败 3 次才停（单次静默重试）
  AUTH_MS: 3000, // 扫码后复查授权间隔
  AUTH_MAX_MS: 120000, // 扫码复查上限
  STOPPING_WARN_MS: 90000, // 「停止中」超过 90s 未回写 → 提示手动刷新
  HIGHLIGHT_CLEAR_MS: 3000, // 新批次定位高亮的持续时长
  PENDING_CHECK_MS: 3000, // 「停止中超时」巡检周期
};

/* -------------------------------------------------------------------------- */
/* 信封与错误码（对接文档 §1 / §8 的唯一落地点；分发逻辑在 utils.toUiAction）        */
/* -------------------------------------------------------------------------- */

/**
 * 成功码。workwx 网关惯例是 '00000'（与同 host 的 offlineMaterial.js 完全一致），
 * 代码里另有历史页面判 '0'，一并兜住，避免联调时「后端明明成功、前端判失败」。
 * 🔴 定义在常量层而不是服务层：utils 要用它做错误分发，服务又 import utils，
 *    放服务里会形成 services ↔ utils 循环依赖。
 */
export const CODE_SUCCESS = '00000';
export const CODE_SUCCESS_LEGACY = '0';

export const isSuccessCode = (code) =>
  `${code}` === CODE_SUCCESS || `${code}` === CODE_SUCCESS_LEGACY;

export const ERR = {
  CYCLE: '07020039',
  NOT_WHITELIST: '07020040',
  ENV_OR_CONFIRM: '07020041',
  WEB_TYPE: '07020042',
  STATE_NOT_ALLOW: '07020043',
  NO_NEED_STOP: '07020044',
  LPAAS_FAIL: '07020045',
  DEPENDENCY_MISSING: '07020046',
  LPAAS_APP_NOT_FOUND: '07020049',
  AUTH_INVALID: '07020050',
  AUTH_IN_PROGRESS: '07020051',
  ITERATION_NO_INVALID: '07020053',
  BRANCH_EMPTY: '07020054',
  SERVICES_EMPTY: '07020055',
  DUPLICATE_REQUEST: '07020056',
  /** 🆕 【临时方案】功能被服务端关闭（手动录入入口未开 / 生产环境硬禁） */
  FEATURE_DISABLED: '07020057',
  REPUBLISH_NOT_ALLOW: '07020058',
  PARAM_INVALID: '99998',
  PARAM_MISSING: '99999',
  SYSTEM_ERROR: '00001',
  DOWNSTREAM_ERROR: '99997',
  LOCK_BUSY: '99996',
};

/**
 * 作废码：出现即说明连的是旧后端或跑的是旧前端。
 * 🔴 不许静默当普通失败处理（这些校验在本期已被废弃，见对接文档 §8 🗑 行）。
 */
export const ERR_DEPRECATED = {
  '07020031': '报告已改选填',
  '07020036': '发布顺序列不再解析',
  '07020037': '已改为依赖悬空警告',
  '07020038': '同迭代可多批次',
  '07020047': '批次倒挂校验已废',
  '07020048': '报告乐观锁已废',
  '07020052': '服务状态已改回大写枚举',
};

/* -------------------------------------------------------------------------- */
/* 文案（按钮 / 提示，页面零硬编码）                                              */
/* -------------------------------------------------------------------------- */

/** 按钮：常显 + 置灰 + tooltip（AC-14），🔴 不许随状态隐藏 */
export const BTN = {
  NEW_PUBLISH: '新增发布',
  BACK_LIST: '返回列表',
  EXPAND: '展开',
  COLLAPSE: '收起',
  QUERY: '查询',
  RESET: '重置',
  RESUME_POLL: '继续自动刷新',
  STOP_BATCH: '停止发布',
  REPUBLISH_BATCH: '发布',
  STOP_SERVICE: '停止',
  RETRY_SERVICE: '重发',
  DETAIL: '查看详情',
  GO_AUTHORIZE: '向用户申请授权',
  RE_AUTHORIZE: '去重新授权',
  CONFIRM_PROD: '确认发布生产',
  PARSE_REPORT: '解析报告',
  REFRESH: '刷新',
  CANCEL: '取消',
  SUBMIT: '确定',
  LPASS_LINK: '↗ LPaaS',
};

/**
 * 迭代号格式（对接文档 §5.2 / §4.2-⑥）：1~9 位正整数，或「主号-子号」（如 119-1，两段各 1~9 位数字）。
 * 🔴 前端只做格式拦，存在性/环境归属由后端判（07020053）；
 *    后端 IterationPageRequest / batch/create 的同名校验需同步放开，否则线上会被 07020053 拦回。
 */
export const ITERATION_NO_RE = /^\d{1,9}(-\d{1,9})?$/;

/** 生产环境分支（仅此一个值会触发二次确认，§5.3） */
export const PROD_BRANCH = 'master';

/**
 * 生产二次确认令牌（G3）的**用途**。
 *
 * 🔴 后端 5 个写操作在生产下一律校验一次性 `confirmToken`，而 token 由
 *    `POST /lpaasPublish/confirm/issue` 签发，并且**绑定「用途 + 目标对象」**：
 *    拿 BATCH_CREATE 换来的 token 去停止批次必被拒（verifyProdConfirm 里逐字比 expectation）。
 *    所以这里 5 个字面量必须与后端 `LpaasPublishConstants.CONFIRM_PURPOSE_*` 完全一致，
 *    少一个字母 = 该操作在生产下必然 07020041。
 * 🔴 历史教训：这里原来是 `CONFIRM_TOKEN_SENTINEL = 'CONFIRMED'` 的硬编码占位串，
 *    后端 Redis 里查不到它 → **生产（master）的发布/停止/重发全量不可用**，而 sit 一切正常，
 *    所以人肉联调测不出来。占位串已删除，token 只能来自 `/confirm/issue`（见 hooks/useProdConfirm.js）。
 */
export const CONFIRM_PURPOSE = {
  BATCH_CREATE: 'BATCH_CREATE',
  BATCH_REPUBLISH: 'BATCH_REPUBLISH',
  BATCH_STOP: 'BATCH_STOP',
  SERVICE_STOP: 'SERVICE_STOP',
  SERVICE_RETRY: 'SERVICE_RETRY',
};

/** 置灰 tooltip 与提示文案（原因必须说清楚，不能只置灰） */
export const TIP = {
  BATCH_NOT_PUBLISHING: '该批次已结束，不能停止',
  BATCH_RUNNING: '该批次正在发布中，不能整批重发',
  BATCH_TERMINATED: '已停止的批次可直接点【发布】',
  /** 批次已 SUCCESS：按 PRD 只有「发布失败」才可点【发布】，这里必须说清为什么置灰 */
  BATCH_DONE: '该批次已完成，不能整批重发',
  SERVICE_NOT_RUNNING: '该服务当前不在执行中，无需停止',
  SERVICE_NOT_FAILED: '只有发布失败/超时/跳过/终止的服务可以重发',
  STOP_IN_PROGRESS: '停止指令已下发，LPaaS 生效需要几秒到几十秒',
  STOPPING_TIMEOUT: 'LPaaS 状态未回写，请稍后手动刷新',
  SELECT_NONE: '至少勾选 1 个应用才能提交',
  ITERATION_NO_INVALID: '迭代号格式：1~9 位数字，或「数字-数字」如 119-1',
  BRANCH_REQUIRED: '发布分支不能为空',
  BRANCH_HINT: 'sit = 测试环境；master = 生产环境，需二次确认',
  /**
   * 分支下拉下的常驻说明。
   * 🔴 后端 2026-10-01 定的新口径：`/app/list` 按**目标环境**返回，切分支必须重拉 ——
   *    环境名或分支名都行（后端都能解析），前端传的是分支值。
   */
  BRANCH_NS_SWITCH:
    '切换分支会重新拉取该环境的应用清单（不同环境的应用 ID 与白名单不同），已勾选会被清空',
  PROD_CONFIRM: '本次将发布到【生产环境】',
  PROD_CONFIRM_CHECK: '我已确认上述影响',
  PROD_REPUBLISH: '重发会重新构建并重启上次已发布成功的服务',
  /**
   * 详情页 4 个写操作的确认框标题（新建发布用上面那句 PROD_CONFIRM）。
   * 🔴 用「动作名」而不是笼统的「发布」：停止和发布的影响完全不同，
   *    用户在确认框里必须一眼看出自己正要干的是哪件事。
   */
  PROD_ACTION_TITLE: (action) =>
    `本次将对【生产环境】执行「${action}」，确认继续？`,
  /** 生产二次确认框里「上述影响」到底指什么：停止类的具体影响（重发类用 PROD_REPUBLISH） */
  PROD_STOP_BATCH_IMPACT: '停止后，本批次未开始的服务会被置为已终止',
  PROD_STOP_SERVICE_IMPACT: '停止后，该服务未开始的阶段会被置为已终止',
  /** 带着 token 仍被拒（token 过期/已用过，或根本不是「缺确认」而是 G2 环境不一致）时的兜底文案 */
  PROD_CONFIRM_RETRY_FAIL: '二次确认未通过，请重新操作或联系运维核对环境配置',
  AUTH_EXPIRING: (d) => `LPaaS 授权将于 ${d} 到期，建议提前重新扫码`,
  AUTH_EXPIRED_ROW: 'LPaaS 授权失效，等服务端重新认证后会自动续跑',
  DEPEND_DANGLING: (names) =>
    `以下依赖未在本次勾选范围内，运行期可能取不到服务：${names}`,
  DEPEND_AUTO_ADD: (names) => `已自动带上它依赖的上游：${names}`,
  DEPEND_AUTO_SELECT: (name) => `已自动勾选它依赖的上游：${name}`,
  APP_NOT_SELECTABLE: (reason) => reason || '该应用当前不可选',
  CACHE_STALE: (cachedAt) => `应用清单拉取于 ${cachedAt || '-'}`,
  /* ---------- 详情页：销毁性操作必须先问一句（方案 §6.3 未定按钮形态，这里是实现选择） ---------- */
  STOP_BATCH_CONFIRM: '停止后本批次未开始的服务会被置为已终止，确定停止？',
  STOP_SERVICE_CONFIRM: '停止后该服务未开始的阶段会被置为已终止，确定停止？',
  /** LPaaS 链接后端可能没给（旧数据）；🔴 空就置灰，绝不前端拼 URL（修 D11） */
  LPASS_NO_URL: '该服务暂无可跳转的 LPaaS 记录',
};

/** 固定展示文案（含空态） */
export const TEXT = {
  PAGE_TITLE: 'LPAAS 发布',
  CONFIG_TITLE: 'LPAAS 发布配置',
  DETAIL_TITLE: '发布详情',
  EMPTY_ITERATION: '暂无迭代记录',
  EMPTY_APP: '暂无应用清单',
  EMPTY_BATCH: '暂无批次记录',
  EMPTY_SERVICE: '本批次暂无服务',
  STAGE_EMPTY: '暂无阶段数据',
  NO_DASH: '-',
  PUBLISH_STARTED: '发布已启动',
  CYCLE_PREFIX: '依赖存在循环：',
  PARSE_FAIL: '解析失败，可手动勾选服务',
  PARSE_EMPTY_LINK: '未填报告链接，请直接手动勾选服务',
  POLL_RUNNING: (sec, waited) =>
    `正在自动刷新（每 ${sec} 秒，已等待 ${waited} 秒）`,
  POLL_STOPPED: '已停止自动刷新',
  POLL_TIMEOUT: '自动刷新已达上限，可稍后刷新查看',
  POLL_FAILED: '自动刷新失败，请手动刷新',
  BATCH_LABEL: (no) => `第 ${no} 批`,
  BATCH_OF_TOTAL: (cur, total) => `第 ${cur} 批 / 共 ${total} 批`,
  /** 列表「发布详情」列的聚合徽标（数字全来自后端 stat，前端不 reduce） */
  STAT_SUCCESS: (n) => `成功 ${n}`,
  STAT_RUNNING: (n) => `进行中 ${n}`,
  STAT_PENDING: (n) => `待发布 ${n}`,
  STAT_FAILED: (n) => `失败 ${n}`,
  STAT_SKIPPED: (n) => `跳过/终止 ${n}`,
  AUTH_GUIDE_TITLE: 'LPaaS 授权',
  AUTH_GUIDE_WAITING: '请使用手机飞书扫码授权，授权完成后自动继续…',
  AUTH_GUIDE_TIMEOUT: '扫码未完成，请重新发起授权',
  /** needAuth 三种成因的正文（首次 / 已过期 / 授权无效），🔴 都不是「发布失败」 */
  AUTH_GUIDE_FIRST: '首次发布需要先完成 LPaaS 授权，授权成功后自动继续',
  AUTH_GUIDE_EXPIRED: 'LPaaS 授权已过期，请重新扫码授权',
  AUTH_GUIDE_INVALID: 'LPaaS 授权无效，请重新扫码授权',
  AUTH_GUIDE_QR_HINT: '用手机飞书扫码，或在新标签打开授权链接',
  AUTH_GUIDE_NO_QR: '授权链接获取中，请稍候…',
  AUTH_GUIDE_CANCEL_TIP: '关闭引导不会产生任何发布记录',
  /** 授权状态查询网络/系统失败（≠ 未授权，不引导扫码，只提示重试） */
  AUTH_CHECK_FAIL: '授权状态查询失败，请稍后重试',
  /* ---------- 🔴 【临时方案】TEMPORARY-2026-10 手动token（文案必须含「临时」字样，避免当成正式链路） ---------- */
  AUTH_GUIDE_MANUAL_TITLE: '临时方案：手动粘贴 LPaaS 凭证',
  AUTH_GUIDE_MANUAL_TIP:
    '🔴 临时方案（节后由扫码授权替代）：把网页登录 LPaaS 后的登录态凭证粘贴到下方，仅用于应急联调；' +
    '扫码通道得到的凭证在这里无效。',
  AUTH_GUIDE_MANUAL_PLACEHOLDER: '粘贴 31 位登录态凭证',
  AUTH_GUIDE_MANUAL_SUBMIT: '保存并校验',
  AUTH_GUIDE_MANUAL_EMPTY: '请输入 LPaaS 访问凭证',
  AUTH_GUIDE_MANUAL_INVALID:
    '凭证无效：请确认为网页登录得到的完整登录态凭证（31 位），扫码/CLI 通道得到的凭证不可用于此',
  AUTH_GUIDE_MANUAL_FAIL: '凭证校验未通过，请确认凭证完整且未过期后重试',
  AUTH_GUIDE_MANUAL_DISABLED:
    '手动录入入口未开启（或当前环境不允许），请改用扫码授权',
  AUTH_GUIDE_MANUAL_TAG: '临时授权',
  AUTH_GUIDE_MANUAL_TOKEN_HINT:
    '凭证只会提交给服务端校验，不会出现在页面日志或埋点里',
  // 🔴 分母是**可选服务数**（AC-10），不是清单总行数（selectionSummary 算好再传进来）
  SELECTED_COUNT: (n, total) => `已选 ${n} / 共 ${total}`,
  APPS_LOADING: '正在获取应用清单…',
  DEPEND_POOL_HINT: '候选来自全量应用清单，不可选的应用不在此列',
  SEARCH_APP: '搜索应用名称/说明',
  ONLY_SELECTABLE: '只看可选',
  APP_DISABLED_TAG: '不可选',
  /** 报告解析结果摘要（选填项，没填就已填的手动清单） */
  PARSE_OK: (n) => `解析到 ${n} 个服务`,
  PARSE_ABNORMAL: (n) => `${n} 行异常`,
  PARSE_WARN: (n) => `${n} 条提示`,
  /* ---------- 表单/筛选字段名（弹窗表单与列表筛选共用同一份，避免两处措辞不一致） ---------- */
  FIELD_ITERATION_NO: '迭代号',
  FIELD_STATUS: '状态',
  FIELD_PUBLISHER: '发布人',
  FIELD_BRANCH: '发布分支',
  FIELD_REPORT_LINK: '报告链接（选填）',
  FIELD_SERVICES: '服务清单',
  PLACEHOLDER_ITERATION_NO: '1~9 位数字或「119-1」，同一迭代号可多次发布',
  PLACEHOLDER_BRANCH: '请选择发布分支',
  PLACEHOLDER_REPORT_LINK: '飞书多维表格报告链接（选填）',
  /**
   * 列表页三个筛选项的底纹（产品 2026-07-31 口径）：
   * 🔴 输入框统一「请输入」、下拉框统一「请选择」，不再共用一句「支持模糊匹配」。
   *    （模糊匹配这个能力本身没变，只是不再占底纹位；见 index.jsx 那条宽松匹配的注释。）
   */
  FILTER_PLACEHOLDER_INPUT: '请输入',
  FILTER_PLACEHOLDER_SELECT: '请选择',
  TOTAL_COUNT: (n) => `共 ${n} 条`,
  SESSION_LOST: '发布会话已失效，请重新打开弹窗',
  /** `/confirm/issue` 回成功但没带 token（不该发生）：宁可拦下，也不拿空串去提交 */
  CONFIRM_TOKEN_EMPTY: '二次确认凭证获取失败，请重试',
  /**
   * 切分支后的提示（🔴 必须说清「为什么勾选没了」）。
   * 原因：appId 是**分环境**的（白名单本体），sit 勾的 appId 拿到 master 发必被 `07020041` 拒，
   * 所以宁可在切换那一刻清干净，也不能让用户填完一整屏才被拒。
   */
  BRANCH_SWITCH_RESET: (b) =>
    `已切换到「${b}」：应用清单已重新拉取，勾选与依赖已清空（不同环境的应用 ID 不同）；如需按报告重填请重新点【解析】`,
  PARSE_TITLE: (t) => `报告：${t}`,
  PARSE_UNMATCHED: (names) => `以下报告行不在当前清单内，已跳过：${names}`,
  PARSE_UNAVAILABLE: (names) =>
    `以下报告行当前不可选，未自动勾选（仍可手动调整）：${names}`, // 分清「不在清单」和「在清单但不可选」
  /* ---------- 详情页（方案 §6.3 / 对接文档 §4.2-⑥⑦⑧ + §7） ---------- */
  DETAIL_MISSING_ID: '缺少迭代号，无法加载详情',
  DETAIL_LOAD_FAIL: '详情加载失败，请稍后重试',
  /** 🔴 详情页的「发布人」是帕加马账号，列表页那列是 LPaaS 授权人 —— 两列不得互抄，标签也必须能区分 */
  FIELD_PUBLISHER_PALMER: '发布人（帕加马账号）',
  // 🔴 后端 `publishDate` 给的是到分钟的日期时间（"2026-09-23 14:02"），所以叫「发布时间」不叫「发布日期」
  FIELD_PUBLISH_TIME: '发布时间',
  FIELD_BRANCH_DISPLAY: '分支',
  FIELD_DURATION: '耗时',
  FIELD_FAIL_REASON: '失败原因',
  FIELD_LPAAS_ORIGIN: 'LPaaS 原文',
  /** 有阶段在跑、没有结束时间 → 只能报「进行中用掉多少」，不许编一个总耗时 */
  DURATION_ELAPSED: (v) => `进行中 ${v}`,
  STOPPING_LABEL: '停止中',
  STOP_BATCH_OK: '已下发停止指令',
  STOP_PARTIAL_TITLE: '部分服务停止失败',
  STOP_RESULT_STOPPED: (n) => `已下发停止指令 ${n} 个服务`,
  REPUBLISH_OK: '已触发整批重发（只重发已结束的服务，发布中的不动）',
  RETRY_OK: '已触发重发',
  /* ---------- 详情页版式（对齐 prototype.html 的视觉，🔴 只借样式不借旧口径交互） ---------- */
  /** 后端已按提交时间倒序下发，第一条即最新批次（展示标，不参与任何判定） */
  BATCH_NEWEST: '最新',
  FIELD_CURRENT_BATCH: '当前批次',
  /** 列表页那列「发布人」= LPaaS 授权人（AC-11）；🔴 详情页已按产品指令不再展示这个人 */
  FIELD_PUBLISHER_LPAAS: 'LPaaS 授权人',
  /* ---------- 详情页基本信息卡（产品 2026-10-01 定：按原型摆 12 格） ---------- */
  /** 🔴 卡里只展「发布人（帕加马账号）」，LPaaS 授权人已按产品指令去掉（两字段后端都给，取哪个归产品定） */
  FIELD_DESC: '描述',
  FIELD_REPORT_LINK_DISPLAY: '报告链接',
  /** 🔴 只有后端**明确给了空串**（= 真没关联）才用这句；字段根本没下发时展「-」，不拿它当“没关联” */
  REPORT_LINK_NONE: '未关联报告',
  FIELD_SERVICE_TOTAL: '服务总数',
  FIELD_STAT_SUCCESS: '成功',
  FIELD_STAT_RUNNING: '进行中',
  FIELD_STAT_OTHER: '其他',
  /**
   * 「其他」合并格。🔴 后端 `stat` 只有 6 个 key，其中**跳过与终止是合并一档 `skippedOrTerminated`**，
   * 所以上文案写「跳过或终止」不拆成两个数 —— 拆了就是前端编的（对接文档 L227 + 产品 2026-10-01 定）。
   */
  STAT_OTHER: (pending, skippedOrTerminated, failed) =>
    `等待 ${pending} / 跳过或终止 ${skippedOrTerminated} / 失败 ${failed}`,
  /** 批次折叠箭头（放常量层，避免 JSX 里散落符号字面量） */
  BATCH_CARET_OPEN: '▲',
  BATCH_CARET_CLOSED: '▼',
  BATCH_COLLAPSE_TIP: '点击批次表头展开/收起（收起不影响后台自动刷新）',
  /* —— 依赖标识（服务行专用）：一期契约没给「依赖类型/强弱」字段，所以只有「有 / 无」两档文案。
   🔴 判有没有只看后端 `dependsDisplay` 给没给串；关系原文一律照后端，不重算、不改写 —— */
  DEP_HAS: '有依赖',
  /**
   * 无依赖标识。🔴 「有没有依赖」只看后端 `dependsDisplay` 给没给串（空 / 「-」即无依赖），
   *    有依赖时直展后端原文（已含「○依赖 xxx」），前端不重拼、不取前几个、不翻译。
   */
  DEP_NONE: '● 无依赖',
  /** 终止/超时原因卡片的小标题（🔴 只放后端 terminateReason，失败原因不捷进来） */
  CARD_TERMINATE_TITLE: '终止原因',
  /** 基本信息卡标题（原型 .info-card h3） */
  INFO_CARD_TITLE: '基本信息',
};

/* -------------------------------------------------------------------------- */
/* 配置页字典（§6.4）                                                            */
/* -------------------------------------------------------------------------- */

/** namespace 一期只 sit / prod 两档 */
export const CONFIG_NAMESPACE_OPTIONS = [
  { value: 'sit', label: 'sit（测试环境）' },
  { value: 'prod', label: 'prod（生产环境）' },
];

/**
 * ⚠️ 这三个字段后端存的是 LPaaS 的 int 枚举，且字典「部分待索取」
 * （lpaas-api-接口文档.md 第九节 Q-5 / 服务端方案 §4.5）。
 * 一期按对接文档默认值展示：deployMode=1 / branchMode=1 / batchType=2。
 * 联调拿到真实字典后只改本表，页面不动；拿不到就原样显示后端返回值，🔴 不许猜语义。
 */
export const CONFIG_DEPLOY_MODE = { 1: '1（默认）' };
export const CONFIG_BRANCH_MODE = { 1: '1（默认）' };
export const CONFIG_BATCH_TYPE = { 2: '2（每批）' };

/** 单服务内分批的帮助文案（PRD 术语澄清，极易被误解成「批次号」） */
export const CONFIG_TIP = {
  BUILD_IMAGE: '构建环境镜像（JDK+Maven），不是应用运行镜像',
  BATCH_COUNT: '单服务内分批 = 多台机器滚动发布，与批次号无关',
  APP_ID: '白名单本体：只有在此表内且启用的 appId 才允许发布',
  DEFAULT_DEPENDS_ON:
    '兜底依赖：仅在「报告未填且用户未选」时生效；留空视为无依赖',
  ENABLED_OFF: '停用即移出白名单，发布弹窗里该应用会变灰不可选',
};
